import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Collapse,IconButton, Typography } from '@mui/material';
import React, { useCallback,useState } from 'react';

import { DetectedScript, detectScriptsInTag } from '../../../../../util/thirdPartyScriptDetection';

interface ScriptDetectorProps {
  iframeRef: React.RefObject<HTMLIFrameElement>;
  tag: string;
}

function ScriptDetector({ iframeRef, tag }: ScriptDetectorProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [detectedScripts, setDetectedScripts] = useState<DetectedScript[]>([]);

  const detectScripts = useCallback(() => {
    // Use shared utility for tag detection
    const results = detectScriptsInTag(tag);

    // Also check iframe content if available
    if (iframeRef?.current?.contentDocument) {
      const scripts = iframeRef.current.contentDocument.getElementsByTagName('script');
      Array.from(scripts).forEach(script => {
        const src = script.getAttribute('src') || '';
        const scriptContent = script.textContent || '';
        // Check script sources and content
        if (src) {
          results.push(...detectScriptsInTag(src).map(r => ({ ...r, location: 'iframe' as const })));
        }
        if (scriptContent) {
          results.push(...detectScriptsInTag(scriptContent).map(r => ({ ...r, location: 'iframe' as const })));
        }
      });
    }

    // Deduplicate by vendor
    const uniqueResults = results.filter((result, index, self) =>
      index === self.findIndex((t) => t.vendor === result.vendor)
    );

    setDetectedScripts(uniqueResults);
  }, [tag, iframeRef]);

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isExpanded) {
      detectScripts();
    }
    setIsExpanded(!isExpanded);
  };

  const getDisplayLocation = (location: 'tag' | 'iframe' | 'adm') => {
    if (location === 'iframe') return 'embedded';
    if (location === 'adm') return 'adm';
    return 'tag';
  };

  return (
    <Box sx={{ mt: 0 }}>
      <Box
        onClick={handleExpandClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          color: '#62A42D',
        }}
      >
        <IconButton
          size="small"
          sx={{
            color: '#62A42D',
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 150ms ease',
            padding: '1px',
          }}
          onClick={handleExpandClick}
        >
          <ExpandMoreIcon />
        </IconButton>
        <Typography sx={{ fontSize: '0.875rem' }}>
          Detect Third-Party Scripts
        </Typography>
      </Box>
      
      <Collapse in={isExpanded}>
        <Box sx={{ pl: 4, mt: 0.5 }}>
          {detectedScripts.length === 0 ? (
            <Typography
              variant="body2"
              sx={{
                py: 0.5,
                color: 'text.secondary',
                fontStyle: 'italic'
              }}
            >
              No third-party scripts detected
            </Typography>
          ) : (
            detectedScripts.map((script) => (
              <Typography
                key={`${script.vendor}-${script.location}${script.details || ''}`}
                variant="body2"
                sx={{
                  py: 0.5,
                  color: 'text.secondary',
                }}
              >
                {script.vendor} ({getDisplayLocation(script.location)})
                {script.details && ` - ${script.details}`}
              </Typography>
            ))
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

export default ScriptDetector;